import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';

import axios from 'axios';
// Global Components
import hljsVuePlugin from '@highlightjs/vue-plugin';

import { plugin, defaultConfig } from '@formkit/vue';
import WootWizard from 'components/ui/Wizard.vue';
import FloatingVue from 'floating-vue';
import WootUiKit from 'dashboard/components';
import App from 'dashboard/App.vue';
import i18nMessages from 'dashboard/i18n';
import { DEFAULT_LOCALE } from 'shared/constants/enabledLocales';
import createAxios from 'dashboard/helper/APIHelper';

import commonHelpers, { isJSONValid } from 'dashboard/helper/commons';
import { sync } from 'vuex-router-sync';
import { createPinia } from 'pinia';
import router, { initalizeRouter } from 'dashboard/routes';
import store from 'dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from 'dashboard/helper/scriptHelpers.js';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';
import { twemojiDirective } from 'shared/helpers/twemoji';

import { vResizeObserver } from '@vueuse/components';
import { directive as onClickaway } from 'vue3-click-away';
import { emitter } from 'shared/helpers/mitt';
import { initializeStaleBundleReload } from 'dashboard/helper/staleBundleReload';

import 'floating-vue/dist/style.css';

const VITE_PRELOAD_RELOAD_KEY = 'aceleres_vite_preload_reload_at';
const VITE_PRELOAD_RELOAD_COOLDOWN = 120000;

// Uma publicação substitui os chunks com hash. Se a aba ainda estiver usando
// o manifesto anterior, recarregue uma única vez para adotar os assets atuais.
window.addEventListener('vite:preloadError', event => {
  event.preventDefault();

  const now = Date.now();
  const lastReloadAt = Number(
    sessionStorage.getItem(VITE_PRELOAD_RELOAD_KEY) || 0
  );
  if (now - lastReloadAt < VITE_PRELOAD_RELOAD_COOLDOWN) return;

  sessionStorage.setItem(VITE_PRELOAD_RELOAD_KEY, String(now));
  window.location.reload();
});

setTimeout(() => {
  sessionStorage.removeItem(VITE_PRELOAD_RELOAD_KEY);
}, VITE_PRELOAD_RELOAD_COOLDOWN);

const i18n = createI18n({
  legacy: false, // https://github.com/intlify/vue-i18n/issues/1902
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: i18nMessages,
});

sync(store, router);

const pinia = createPinia();

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(pinia);
app.use(router);
initializeStaleBundleReload({ router, entryUrl: import.meta.url });

// [VITE] Disabled this, need to renable later
if (window.errorLoggingConfig) {
  Sentry.init({
    app,
    dsn: window.errorLoggingConfig,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

app.use(VueDOMPurifyHTML, domPurifyConfig);
app.use(WootUiKit);
app.use(
  plugin,
  defaultConfig({
    rules: {
      JSON: ({ value }) => isJSONValid(value),
    },
  })
);
app.use(FloatingVue, {
  instantMove: true,
  arrowOverflow: false,
  disposeTimeout: 5000000,
});
app.use(hljsVuePlugin);

app.component('woot-wizard', WootWizard);
app.component('fluent-icon', FluentIcon);

app.directive('resize', vResizeObserver);
app.directive('on-clickaway', onClickaway);
app.directive('twemoji', twemojiDirective);

// load common helpers into js
commonHelpers();
window.WootConstants = constants;
window.axios = createAxios(axios);
Object.defineProperty(window, '__aceleresShowToast', {
  configurable: true,
  value: (message, action = null) => {
    emitter.emit('newToastMessage', { message, action });
  },
});
// [VITE] Disabled this we don't need it, we can use `useEmitter` directly
// app.prototype.$emitter = emitter;

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();

const mountDashboard = () => {
  app.mount('#app');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountDashboard, { once: true });
} else {
  mountDashboard();
}
